import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import {
  Controller,
  useForm,
  useWatch,
} from 'react-hook-form';
import {
  getCountries,
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';
import labels from 'react-phone-number-input/locale/es';

import {
  QuoteSubmissionError,
  submitQuote,
  type FieldErrors,
} from '../api/submitQuote';
import {
  createDefaultValues,
  quoteFormSchema,
  type QuoteFormInput,
  type QuoteFormPayload,
} from '../schema/quoteFormSchema';

interface QuoteFormProps {
  onSubmitted?: () => void;
  submit?: (payload: QuoteFormPayload) => Promise<void>;
}

const FIELD_NAMES: (keyof QuoteFormInput)[] = [
  'name',
  'company',
  'email',
  'phone',
  'inquiryType',
  'productType',
  'quantity',
  'comments',
];

const ErrorMessage = ({ id, message }: { id: string; message?: string }) => {
  if (!message) return null;
  return (
    <span className="field-error" id={id} role="alert">
      {message}
    </span>
  );
};

const countryToFlag = (countryCode: Country) =>
  [...countryCode]
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');

export const QuoteForm = ({
  onSubmitted,
  submit = submitQuote,
}: QuoteFormProps) => {
  const [country, setCountry] = useState<Country>('UY');
  const [isComplete, setIsComplete] = useState(false);
  const [formError, setFormError] = useState('');

  const countryOptions = useMemo(
    () =>
      getCountries()
        .map((countryCode) => ({
          value: countryCode,
          name: String(labels[countryCode]),
          label: `${countryToFlag(countryCode)} +${getCountryCallingCode(countryCode)}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [],
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<QuoteFormInput, unknown, QuoteFormPayload>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: createDefaultValues(),
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const commentsLength = useWatch({ control, name: 'comments' })?.length ?? 0;

  const applyServerErrors = (fieldErrors: FieldErrors) => {
    FIELD_NAMES.forEach((fieldName) => {
      const message = fieldErrors[fieldName];
      if (message) setError(fieldName, { type: 'server', message });
    });
  };

  const onValid = async (payload: QuoteFormPayload) => {
    setFormError('');

    if (payload.website) {
      setIsComplete(true);
      return;
    }

    try {
      await submit(payload);
      reset(createDefaultValues());
      setIsComplete(true);
      onSubmitted?.();
    } catch (error) {
      if (error instanceof QuoteSubmissionError) {
        applyServerErrors(error.fieldErrors);
        setFormError(error.message);
        return;
      }

      setFormError(
        'No pudimos enviar tu solicitud. Intentá nuevamente en unos minutos.',
      );
    }
  };

  const onInvalid = () => {
    setFormError('Revisá los campos marcados antes de continuar.');
  };

  const startAnotherRequest = () => {
    reset(createDefaultValues());
    setCountry('UY');
    setFormError('');
    setIsComplete(false);
  };

  if (isComplete) {
    return (
      <section
        className="quote-card success-panel"
        aria-labelledby="success-title"
        aria-live="polite"
      >
        <p className="eyebrow">SOLICITUD ENVIADA</p>
        <h1 id="success-title">Recibimos tu solicitud</h1>
        <p>Gracias por contactarte.</p>
        <button className="secondary-button" type="button" onClick={startAnotherRequest}>
          Enviar otra consulta
        </button>
      </section>
    );
  }

  return (
    <section className="quote-card" aria-labelledby="form-title">
      <form
        className="quote-form"
        noValidate
        aria-busy={isSubmitting}
        onSubmit={handleSubmit(onValid, onInvalid)}
      >
        <h1 className="form-intro" id="form-title">
          Completá el formulario y cotizá tus próximos regalos con el logo de tu
          empresa
        </h1>

        {formError && (
          <div className="form-alert" role="alert" tabIndex={-1}>
            {formError}
          </div>
        )}

        <div className="form-grid">
          <div className="field">
            <label htmlFor="name">
              Nombre <span aria-hidden="true">*</span>
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              required
              maxLength={100}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'name-error' : undefined}
              placeholder="Tu nombre"
              {...register('name')}
            />
            <ErrorMessage id="name-error" message={errors.name?.message} />
          </div>

          <div className="field">
            <label htmlFor="company">
              Empresa <span aria-hidden="true">*</span>
            </label>
            <input
              id="company"
              type="text"
              autoComplete="organization"
              required
              maxLength={120}
              aria-invalid={Boolean(errors.company)}
              aria-describedby={errors.company ? 'company-error' : undefined}
              placeholder="Nombre de tu empresa"
              {...register('company')}
            />
            <ErrorMessage id="company-error" message={errors.company?.message} />
          </div>

          <div className="field">
            <label htmlFor="email">
              Email <span aria-hidden="true">*</span>
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              maxLength={254}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'email-error' : undefined}
              placeholder="tu@email.com"
              {...register('email')}
            />
            <ErrorMessage id="email-error" message={errors.email?.message} />
          </div>

          <div className="field">
            <label htmlFor="phone">
              Teléfono <span aria-hidden="true">*</span>
            </label>
            <div className="phone-control">
              <label className="sr-only" htmlFor="phone-country">
                País del teléfono
              </label>
              <select
                id="phone-country"
                className="country-select"
                value={country}
                aria-label={`País del teléfono: ${labels[country]}`}
                onChange={(event) => {
                  setCountry(event.target.value as Country);
                  setValue('phone', '', { shouldValidate: false });
                }}
              >
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value} title={option.name}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    id="phone"
                    className="phone-input"
                    country={country}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(value) => field.onChange(value ?? '')}
                    autoComplete="tel"
                    required
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                    placeholder="Número de teléfono"
                  />
                )}
              />
            </div>
            <ErrorMessage id="phone-error" message={errors.phone?.message} />
          </div>

          <div className="field field--quantity">
            <label htmlFor="quantity">
              Cantidad estimada <span aria-hidden="true">*</span>
            </label>
            <input
              id="quantity"
              type="number"
              inputMode="numeric"
              min="1"
              max="999999"
              step="1"
              required
              aria-invalid={Boolean(errors.quantity)}
              aria-describedby={errors.quantity ? 'quantity-error' : undefined}
              placeholder="Ej. 50"
              {...register('quantity')}
            />
            <ErrorMessage id="quantity-error" message={errors.quantity?.message} />
          </div>

          <div className="field field--full">
            <div className="label-row">
              <label htmlFor="comments">Comentarios adicionales</label>
              <span aria-live="polite">{commentsLength}/1000</span>
            </div>
            <textarea
              id="comments"
              rows={4}
              maxLength={1000}
              aria-invalid={Boolean(errors.comments)}
              aria-describedby={errors.comments ? 'comments-error' : undefined}
              placeholder="Contanos detalles, fechas o preferencias"
              {...register('comments')}
            />
            <ErrorMessage id="comments-error" message={errors.comments?.message} />
          </div>
        </div>

        <div className="honeypot" aria-hidden="true">
          <label htmlFor="website">Sitio web</label>
          <input
            id="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register('website')}
          />
        </div>
        <input type="hidden" {...register('inquiryType')} />
        <input type="hidden" {...register('productType')} />
        <input type="hidden" {...register('formStartedAt')} />

        <p className="required-note">
          <span aria-hidden="true">*</span> Campos obligatorios
        </p>

        <button className="submit-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Enviando…' : 'Pedir cotización'}
        </button>
      </form>
    </section>
  );
};
